import { useCallback, useEffect, useState } from "react";
import { IDIOMA, LOCALE, tr } from "../i18n/idioma";

/* Piezas comunes de las guías prácticas (sección /guias, desde 2026-10-31).
   Cada guía es un renderer congelado (src/guias/<slug>/Renderer.jsx) + su modelo puro
   (model.js) + un dataset vivo (public/data/guias/<slug>.json, inglés en en/<slug>.json).
   Aquí: carga del dataset, paso en la URL (#paso-3), modo ahorro, cabecera, artículo del paso,
   cifras con su fuente, estado de revisión y resumen en audio.

   `contexto`: "web" (el sitio) u "offline" (el HTML autocontenido para Zapya o el Paquete,
   scripts/exportar-guias.mjs): sin audio ni enlaces internos. */

const BASE = process.env.PUBLIC_URL || "";
export const fmt = (v, d = 0) => Number(v).toLocaleString(LOCALE(), { maximumFractionDigits: d, minimumFractionDigits: 0 });

export function useGuia(slug, embebido) {
  const [estado, setEstado] = useState(embebido ? { datos: embebido } : { cargando: true });
  useEffect(() => {
    if (embebido) return undefined;
    let vivo = true;
    const pedir = (en) => fetch(`${BASE}/data/guias/${en ? "en/" : ""}${slug}.json`, { cache: "no-cache" })
      .then((r) => { if (!r.ok || !(r.headers.get("content-type") || "").includes("json")) throw new Error(String(r.status)); return r.json(); });
    (IDIOMA === "en" ? pedir(true).catch(() => pedir(false)) : pedir(false))
      .then((datos) => vivo && setEstado({ datos }))
      .catch((e) => vivo && setEstado({ error: e.message }));
    return () => { vivo = false; };
  }, [slug, embebido]);
  return estado;
}

// Paso actual, sincronizado con #paso-N (se puede compartir o volver al mismo paso).
export function usePaso(total) {
  const leer = () => { const m = /^#paso-(\d+)$/.exec(window.location.hash); return m ? Math.min(total - 1, Math.max(0, Number(m[1]) - 1)) : 0; };
  const [paso, setPaso] = useState(leer);
  useEffect(() => {
    const cambio = () => setPaso(leer());
    window.addEventListener("hashchange", cambio);
    return () => window.removeEventListener("hashchange", cambio);
  });
  const ir = useCallback((n) => {
    const k = Math.min(total - 1, Math.max(0, n));
    setPaso(k);
    try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}#paso-${k + 1}`); } catch { /* file:// sin historial */ }
  }, [total]);
  useEffect(() => {
    const tecla = (e) => {
      if (/INPUT|SELECT|TEXTAREA/.test(document.activeElement?.tagName || "")) return;
      if (e.key === "ArrowRight") ir(paso + 1); else if (e.key === "ArrowLeft") ir(paso - 1);
    };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [paso, ir]);
  return [paso, ir];
}

// Modo ahorro: se activa solo con «ahorro de datos», con 2 GB de memoria o menos, o si el
// sistema pide menos movimiento. Se puede cambiar a mano.
export function useAhorro() {
  const [ahorro, setAhorro] = useState(() => {
    try {
      return !!(navigator.connection?.saveData || (navigator.deviceMemory && navigator.deviceMemory <= 2)
        || window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch { return false; }
  });
  return [ahorro, setAhorro];
}

export const GUIA_CSS = `
.g{--g-escena:#ebe4d8; --g-resalte:#e08a1e; --g-linea:#cdbfae; --g-peligro:#a8251c; --g-peligro-bg:#f7e1dc;
  --g-ok:#2e7d4f; --g-borde:#fcf9f4; padding-bottom:24px;}
.g-head{padding:34px 0 22px; max-width:820px;}
.g-head h1{margin:12px 0 14px;}
.g-chips{display:flex; gap:8px; flex-wrap:wrap; margin-top:18px; align-items:center;}
.g-chips .y-badge{white-space:normal; line-height:1.4;}
.g-borrador{display:flex; gap:12px; align-items:flex-start; background:#f6e3c4; color:#5e3a08; border-radius:16px; padding:14px 18px;
  margin:4px 0 22px; font-size:14.5px; line-height:1.5;}
.g-borrador b{font:600 10.5px var(--y-mono); letter-spacing:.1em; text-transform:uppercase; white-space:nowrap; padding-top:3px;}
.g-sec{margin-top:40px;}
.g-sec > .y-eyebrow{color:var(--y-gui-ink);}
.g-sec h2{margin:8px 0 10px;}
.g-sec > p.g-intro{color:var(--y-ink-2); max-width:720px; margin:0 0 18px; line-height:1.55;}
.g-guia{display:grid; grid-template-columns:minmax(0,1.25fr) minmax(0,1fr); gap:28px; align-items:start; margin-top:12px;}
.g-escenario{position:sticky; top:12px;}
.g-vista{position:relative; border-radius:22px; overflow:hidden; background:var(--g-escena); box-shadow:var(--y-shadow);}
.g-vista canvas{display:block; width:100%; cursor:grab;}
.g-vista canvas:active{cursor:grabbing;}
.g-pista{position:absolute; top:10px; right:12px; font:500 10px var(--y-mono); letter-spacing:.08em; text-transform:uppercase;
  color:var(--y-muted); background:rgba(252,249,244,.8); padding:4px 8px; border-radius:999px; pointer-events:none;}
.g-estado{display:flex; gap:10px; align-items:center; padding:10px 14px; font:500 12.5px var(--y-mono); line-height:1.4;
  background:var(--y-surface); border-top:1px solid var(--y-rule); min-height:44px;}
.g-estado i{flex-shrink:0; width:9px; height:9px; border-radius:50%; background:var(--g-ok);}
.g-estado[data-e="riesgo"] i{background:var(--g-peligro);}
.g-estado[data-e="riesgo"]{color:var(--g-peligro);}
.g-estado[data-e="cortado"] i{background:var(--y-subtle);}
.g-nav{display:flex; align-items:center; gap:10px; margin-top:14px;}
.g-nav .y-btn{padding:10px 16px;}
.g-nav .y-btn:disabled{opacity:.35; cursor:default;}
.g-puntos{display:flex; gap:6px; flex:1; justify-content:center; flex-wrap:wrap;}
.g-puntos button{width:30px; height:30px; border-radius:50%; border:0; cursor:pointer; font:600 12px var(--y-mono);
  background:var(--y-surface-2); color:var(--y-muted);}
.g-puntos button.hecho{background:var(--y-gui-bg); color:var(--y-gui-ink);}
.g-puntos button[aria-current]{background:var(--y-gui-ink); color:#fff;}
.g-herr{display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; align-items:center;}
.g-herr .g-link{font:500 12px var(--y-mono); background:none; border:0; color:var(--y-muted); cursor:pointer; padding:6px 4px;
  text-decoration:underline; text-underline-offset:3px;}
.g-herr .g-link:hover{color:var(--y-accent);}
.g-paso{background:var(--y-surface); border-radius:24px; padding:24px 26px; box-shadow:var(--y-shadow);}
.g-paso h2{font-size:clamp(22px,2.6vw,28px); font-weight:800; letter-spacing:-.02em; line-height:1.15; margin:8px 0 12px;}
.g-paso h3{font:600 11px var(--y-mono); letter-spacing:.1em; text-transform:uppercase; color:var(--y-muted); margin:20px 0 8px;}
.g-paso .g-cuerpo{font-size:16px; line-height:1.6; color:var(--y-ink-2); margin:0;}
.g-checks{list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:8px;}
.g-checks label{display:flex; gap:11px; align-items:flex-start; cursor:pointer; font-size:15px; line-height:1.5;}
.g-checks input{width:20px; height:20px; margin-top:2px; flex-shrink:0; accent-color:var(--y-gui-ink);}
.g-checks input:checked + span{color:var(--y-subtle); text-decoration:line-through; text-decoration-color:var(--y-rule);}
.g-quien{display:inline-block; font:500 9.5px var(--y-mono); letter-spacing:.07em; text-transform:uppercase; padding:2px 7px;
  border-radius:5px; margin-left:6px; vertical-align:1px; white-space:nowrap; text-decoration:none!important;}
.g-quien--cia_guia{background:#e3e7f2; color:#2b4472;}
.g-quien--cia_web{background:#2b4472; color:#fff;}
.g-quien--yamacoa{background:var(--y-dir-bg); color:var(--y-dir-ink);}
.g-nota{display:block; font-size:13.5px; color:var(--y-muted); margin-top:3px;}
.g-orden{margin:0; padding-left:22px; line-height:1.6;}
.g-caja{border-radius:16px; padding:14px 16px; margin-top:14px; font-size:14.5px; line-height:1.55;}
.g-caja p{margin:0;}
.g-caja b.g-tag{display:block; font:600 10.5px var(--y-mono); letter-spacing:.1em; text-transform:uppercase; margin-bottom:4px;}
.g-peligro{background:var(--g-peligro-bg); color:#5c1712;}
.g-peligro b.g-tag{color:var(--g-peligro);}
.g-error{background:var(--y-surface-2); color:var(--y-ink-2);}
.g-error b.g-tag{color:var(--y-muted);}
.g-red{background:var(--y-gui-bg); color:var(--y-gui-head);}
.g-red b.g-tag{color:var(--y-gui-ink);}
.g-audio{display:flex; gap:16px; align-items:center; background:var(--y-gui-bg); border-radius:20px; padding:16px 20px; margin-top:6px; flex-wrap:wrap;}
.g-audio p{margin:0; font-size:14px; line-height:1.45; color:var(--y-gui-head); flex:1; min-width:200px;}
.g-audio p b{display:block; font:600 10.5px var(--y-mono); letter-spacing:.1em; text-transform:uppercase; color:var(--y-gui-ink); margin-bottom:2px;}
.g-audio a{font:600 13px var(--y-font); color:var(--y-gui-head);}
.g-tabla{width:100%; border-collapse:collapse; font-size:14px; background:var(--y-surface); border-radius:18px; overflow:hidden; box-shadow:var(--y-shadow);}
.g-tabla th{font:600 10.5px var(--y-mono); letter-spacing:.08em; text-transform:uppercase; color:var(--y-muted); text-align:left; padding:12px 14px; background:var(--y-surface-2);}
.g-tabla td{padding:11px 14px; border-top:1px solid var(--y-rule); vertical-align:top; line-height:1.45;}
.g-tabla td small{display:block; color:var(--y-muted); font-size:12.5px; margin-top:3px;}
.g-conf{font:600 10px var(--y-mono); letter-spacing:.08em; text-transform:uppercase; padding:3px 8px; border-radius:6px; white-space:nowrap;}
.g-conf--alta{background:var(--y-auto-bg); color:var(--y-auto-ink);}
.g-conf--media{background:#f6e3c4; color:#7a4a0c;}
.g-conf--baja{background:var(--g-peligro-bg); color:var(--g-peligro);}
.g-pie{margin-top:40px; padding-top:18px; border-top:1px solid var(--y-rule); font-size:13.5px; line-height:1.6; color:var(--y-muted); max-width:820px;}
.g-pie p{margin:0 0 10px;}
.g-pie a{color:inherit;}
.g-calc{display:grid; grid-template-columns:1fr 1fr; gap:18px;}
.g-panel{background:var(--y-surface); border-radius:22px; padding:20px 22px; box-shadow:var(--y-shadow);}
.g-campo{display:flex; flex-direction:column; gap:5px; margin-bottom:14px;}
.g-campo label{font-weight:600; font-size:14px;}
.g-campo small{color:var(--y-muted); font-size:12.5px;}
.g-campo input,.g-campo select{font:500 16px var(--y-font); padding:9px 12px; border-radius:10px; border:1px solid var(--y-rule);
  background:var(--y-bg); color:var(--y-ink); width:100%;}
.g-res{display:flex; flex-direction:column; padding:12px 0; border-bottom:1px solid var(--y-rule);}
.g-res:last-child{border-bottom:0;}
.g-res .k{font:500 11px var(--y-mono); letter-spacing:.08em; text-transform:uppercase; color:var(--y-muted);}
.g-res .v{font-size:26px; font-weight:800; letter-spacing:-.02em; margin:2px 0;}
.g-res .f{font:400 12px var(--y-mono); color:var(--y-subtle);}
.g-aviso{background:#f6e3c4; color:#5e3a08; border-radius:12px; padding:10px 14px; font-size:13.5px; line-height:1.5; margin:10px 0 0;}
.g-rango{width:100%; accent-color:var(--y-gui-ink);}
.g-seg{display:flex; gap:6px; flex-wrap:wrap; margin-top:12px;}
@media (max-width:920px){
  .g-guia{grid-template-columns:1fr;}
  .g-escenario{position:static;}
  .g-calc{grid-template-columns:1fr;}
}
@media (max-width:520px){
  .g-nav{flex-wrap:wrap; justify-content:space-between;}
  .g-puntos{order:-1; flex-basis:100%; margin-bottom:4px;}
  .g-paso{padding:20px 18px;}
  .g-tabla{font-size:13px;}
  .g-tabla th:nth-child(3), .g-tabla td:nth-child(3){display:none;}
}
`;

const QUIEN = {
  cia_guia: () => tr("Guía de la CIA, según la prensa", "CIA guide, as reported"),
  cia_web: () => tr("cia.gov, literal", "cia.gov, verbatim"),
  yamacoa: () => tr("Nota de Yamacoa", "Yamacoa note"),
};
export function Quien({ q }) {
  if (!q || !QUIEN[q]) return null;
  return <span className={`g-quien g-quien--${q}`}>{QUIEN[q]()}</span>;
}

// Texto con <code>…</code> permitido (lo escribe la redacción en el JSON, no un tercero).
const conCodigo = (t) => String(t).split(/(<code>.*?<\/code>)/g).map((s, i) =>
  s.startsWith("<code>") ? <code key={i}>{s.slice(6, -7)}</code> : s);
export const Texto = ({ t }) => <>{conCodigo(t)}</>;

export function Cabecera({ d, chips = [] }) {
  const m = d.meta;
  return (
    <header className="g-head">
      <p className="y-eyebrow" style={{ color: "var(--y-gui-ink)" }}>
        {tr("Guía práctica", "Practical guide")} · {String(d.numero).padStart(2, "0")} · {m.etiqueta}
      </p>
      <h1 className="y-h1">{m.titulo}</h1>
      <p className="y-lede">{m.entradilla}</p>
      <div className="g-chips">
        <span className="y-badge y-badge--gui">{tr("Guía práctica", "Practical guide")}</span>
        {chips.filter(Boolean).map((c) => <span key={c} className="y-badge y-badge--neutro">{c}</span>)}
      </div>
    </header>
  );
}

export function Borrador({ d }) {
  const r = d.revision;
  if (r.estado === "revisada") {
    return <p className="y-meta" style={{ margin: "0 0 18px" }}>{tr("Revisada por", "Reviewed by")} {r.revisado_por} · {r.fecha_revision}</p>;
  }
  return (
    <div className="g-borrador" role="note">
      <b>{tr("Borrador", "Draft")}</b>
      <span>{r.aviso}</span>
    </div>
  );
}

export function AudioGuia({ d, slug, contexto, BotonPlay }) {
  const a = d.meta.audio;
  const url = `${BASE}/audio/guias/${IDIOMA}/${slug}.mp3`;
  const [hay, setHay] = useState(false);
  useEffect(() => {
    if (contexto !== "web" || !a) return undefined;
    let vivo = true;
    // Pages responde index.html (200) a lo que no existe: solo vale si es audio de verdad.
    fetch(url, { method: "HEAD" }).then((r) => vivo && setHay(r.ok && (r.headers.get("content-type") || "").startsWith("audio/")))
      .catch(() => {});
    return () => { vivo = false; };
  }, [url, contexto, a]);
  if (!a || !hay || !BotonPlay) return null;
  const min = a.duracion_s ? Math.max(1, Math.round(a.duracion_s / 60)) : null;
  return (
    <div className="g-audio">
      <BotonPlay pista={{ id: `guia:${slug}:${IDIOMA}`, titulo: d.meta.titulo, tipo: tr("Guía práctica", "Practical guide"), audio: url }} oscuro
        etiqueta={tr(`resumen de ${d.meta.titulo}`, `summary of ${d.meta.titulo}`)} />
      <p>
        <b>{a.titulo}{min ? ` · ${min} min` : ""}</b>
        {a.descripcion} {tr("Voz sintética (Kokoro).", "Synthetic voice (Kokoro).")}
      </p>
      {a.descargable && (
        <a href={url} download={`yamacoa-${slug}-${IDIOMA}.mp3`}>{tr("Descargar MP3", "Download MP3")}</a>
      )}
    </div>
  );
}

export function Navegacion({ pasos, paso, ir, children }) {
  return (
    <>
      <div className="g-nav">
        <button type="button" className="y-btn y-btn--ghost" onClick={() => ir(paso - 1)} disabled={paso === 0}>‹ {tr("Anterior", "Back")}</button>
        <div className="g-puntos" role="group" aria-label={tr("Pasos", "Steps")}>
          {pasos.map((p, i) => (
            <button key={p.id} type="button" className={i < paso ? "hecho" : ""} aria-current={i === paso ? "step" : undefined}
              aria-label={`${tr("Paso", "Step")} ${i + 1}: ${p.titulo}`} onClick={() => ir(i)}>{i + 1}</button>
          ))}
        </div>
        <button type="button" className="y-btn" onClick={() => ir(paso + 1)} disabled={paso === pasos.length - 1}>{tr("Siguiente", "Next")} ›</button>
      </div>
      <div className="g-herr">{children}</div>
    </>
  );
}

export function Herramienta({ onClick, children }) {
  return <button type="button" className="g-link" onClick={onClick}>{children}</button>;
}
export function Interruptor({ activo, onClick, children }) {
  return <button type="button" className="y-chip" aria-pressed={activo} onClick={onClick}>{children}</button>;
}

const item = (c) => (typeof c === "string" ? { t: c } : c);

export function Paso({ p, i, total, rotulo }) {
  return (
    <article className="g-paso" aria-live="polite">
      <p className="y-eyebrow" style={{ color: "var(--y-gui-ink)" }}>{rotulo || tr("Paso", "Step")} {i + 1} {tr("de", "of")} {total}</p>
      <h2>{p.titulo}</h2>
      <p className="g-cuerpo"><Texto t={p.cuerpo} /> <Quien q={p.quien} /></p>
      {p.orden && (
        <>
          <h3>{p.orden_titulo || tr("En este orden", "In this order")}</h3>
          <ol className="g-orden">{p.orden.map((o) => <li key={o}>{o}</li>)}</ol>
        </>
      )}
      <h3>{tr("Hazlo así", "How to do it")}</h3>
      <ul className="g-checks">
        {p.checks.map(item).map((c, k) => (
          <li key={k}>
            <label>
              <input type="checkbox" />
              <span><Texto t={c.t} /><Quien q={c.quien} />{c.nota && <span className="g-nota"><Texto t={c.nota} /></span>}</span>
            </label>
          </li>
        ))}
      </ul>
      {p.red && <div className="g-caja g-red"><b className="g-tag">{p.red_titulo || tr("Qué ve la red", "What the network sees")}</b><p><Texto t={p.red} /></p></div>}
      {p.despues && <div className="g-caja g-red"><b className="g-tag">{tr("Después", "Afterwards")}</b><p><Texto t={p.despues} /> <Quien q={p.despues_quien} /></p></div>}
      {p.peligro && <div className="g-caja g-peligro"><b className="g-tag">{tr("Peligro", "Danger")}</b><p><Texto t={p.peligro} /></p></div>}
      {p.error && <div className="g-caja g-error"><b className="g-tag">{tr("Error común", "Common mistake")}</b><p><Texto t={p.error} /></p></div>}
    </article>
  );
}

const CONF = { alta: () => tr("Alta", "High"), media: () => tr("Media", "Medium"), baja: () => tr("Baja", "Low") };
export function Cifras({ d }) {
  if (!d.cifras?.length) return null;
  const fuentes = Object.fromEntries((d.fuentes || []).map((f) => [f.id, f]));
  return (
    <section className="g-sec" aria-labelledby="g-cifras">
      <p className="y-eyebrow">{tr("De dónde sale cada número", "Where each number comes from")}</p>
      <h2 id="g-cifras" className="y-h2">{tr("Cifras, fuentes y confianza", "Figures, sources and confidence")}</h2>
      <p className="g-intro">{tr("Cada cifra de la guía, con su fuente y lo seguros que estamos de ella. «Baja» quiere decir que hay un solo estudio o que no se pudo comprobar en la fuente original: tómala como orientación.",
        "Every figure in the guide, with its source and how sure we are of it. “Low” means there is a single study or it could not be checked against the original source: treat it as a rough guide.")}</p>
      <div style={{ overflowX: "auto" }}>
        <table className="g-tabla">
          <thead><tr><th>{tr("Cifra", "Figure")}</th><th>{tr("Valor", "Value")}</th><th>{tr("Fuente", "Source")}</th><th>{tr("Confianza", "Confidence")}</th></tr></thead>
          <tbody>
            {d.cifras.map((c) => {
              const f = fuentes[c.fuente];
              return (
                <tr key={c.id}>
                  <td>{c.cifra}{c.nota && <small>{c.nota}</small>}</td>
                  <td style={{ fontWeight: 600 }}>{c.valor}</td>
                  <td>{f ? (f.url ? <a href={f.url} target="_blank" rel="noopener noreferrer">{f.nombre}</a> : f.nombre) : c.fuente}</td>
                  <td><span className={`g-conf g-conf--${c.confianza}`}>{CONF[c.confianza]()}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function Pie({ d, contexto, offline }) {
  return (
    <footer className="g-pie">
      {d.fuentes?.length > 0 && (
        <p><b>{tr("Fuentes:", "Sources:")}</b>{" "}
          {d.fuentes.map((f, i) => (
            <span key={f.id}>{i > 0 && "; "}{f.url ? <a href={f.url} target="_blank" rel="noopener noreferrer">{f.nombre}</a> : f.nombre}{f.fecha ? ` (${f.fecha})` : ""}</span>
          ))}.
        </p>
      )}
      {d.pie?.map((t) => <p key={t}><Texto t={t} /></p>)}
      {d.correcciones?.length > 0 && (
        <p><b>{tr("Correcciones:", "Corrections:")}</b> {d.correcciones.map((c) => `${c.fecha}: ${c.texto}`).join(" · ")}</p>
      )}
      {d.sin_verificar?.length > 0 && (
        <p><b>{tr("Pendiente de verificar:", "Not yet verified:")}</b> {d.sin_verificar.join("; ")}.</p>
      )}
      <p className="y-meta">
        {tr("Estado", "Status")}: {d.revision.estado === "revisada" ? tr("revisada", "reviewed") : tr("borrador", "draft")} ·
        {" "}{tr("Actualizada", "Updated")} {d.meta.actualizado} · {tr("Modelo", "Model")} v{d.version_modelo}
        {contexto === "web" && offline && <> · <a href={offline}>{tr("Descargar la guía para verla sin internet", "Download the guide to use offline")}</a></>}
        {contexto === "offline" && <> · {tr("Copia sin conexión de yamacoa.com/guias", "Offline copy of yamacoa.com/guias")}</>}
      </p>
    </footer>
  );
}

export function Cargando({ estado }) {
  if (estado.error) {
    return <div className="y-wrap"><p className="y-vacio" style={{ marginTop: 32 }}>{tr("No se pudo cargar la guía. Comprueba la conexión y vuelve a intentarlo.", "The guide could not be loaded. Check your connection and try again.")}</p></div>;
  }
  return <div className="ym-cargando" role="status">{tr("Cargando…", "Loading…")}</div>;
}
